import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/training`;
const OPTIONS = { withCredentials: true };
const label = (value) => value.replace(/_/g, " ");
const failure = (error) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : detail?.message || "Could not save this change. Please try again.";
};

// Lesson-scoped, optional close-out step. All identity and gate values come
// from the server; recording an observation never automatically grants ownership.
export default function IndependentPassObservation({ lessonId }) {
  const [context, setContext] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [competencyId, setCompetencyId] = useState("");
  const [responsibilityId, setResponsibilityId] = useState("");
  const [prompting, setPrompting] = useState("");
  const [conditions, setConditions] = useState({});
  const [note, setNote] = useState("");
  const [revokeReason, setRevokeReason] = useState("");
  const locked = useRef(false);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const current = ++generation.current;
    const { data } = await axios.get(`${API}/instructor/lesson/${encodeURIComponent(lessonId)}`, OPTIONS);
    if (generation.current === current) setContext(data);
  }, [lessonId]);

  useEffect(() => {
    setContext(null);
    setError("");
    load().catch((e) => setError(failure(e)));
    return () => { generation.current += 1; };
  }, [load]);

  const competency = context?.competencies?.find((c) => c.id === competencyId);
  const responsibility = competency?.responsibilities?.find((r) => r.id === responsibilityId);
  const observation = responsibility?.lesson_confirmation;
  const tags = Object.values(conditions).filter(Boolean);

  const act = async (operation, success) => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true); setError(""); setMessage("");
    try {
      await operation();
      // Disable stale transfer decisions if the authoritative refresh fails.
      setContext(null);
      await load();
      setMessage(success);
    } catch (e) { setError(failure(e)); }
    finally { locked.current = false; setBusy(false); }
  };

  const record = () => act(() => axios.post(`${API}/instructor-confirmation`, {
    learner_id: context.learner_id, lesson_id: context.lesson_id,
    competency_id: competency.id, responsibility_id: responsibility.id,
    learner_owned_in_attempt: true, prompting_level: prompting,
    condition_tags: tags, observation_note: note.trim(),
  }, OPTIONS), "Your observed independent pass is saved. Ownership has not changed.");

  const transfer = () => act(() => axios.post(`${API}/responsibility-transfer`, {
    learner_id: context.learner_id, competency_id: competency.id,
    responsibility_id: responsibility.id, direction: "grant",
  }, OPTIONS), "Responsibility transferred to the learner.");

  const revoke = () => act(() => axios.post(
    `${API}/instructor-confirmation/${encodeURIComponent(observation.id)}/revoke`,
    { reason: revokeReason.trim() }, OPTIONS,
  ), "Observation withdrawn. Current ownership has been checked again.");

  const fieldClass = "block w-full mt-1 p-3 rounded bg-slate-900 text-white border border-slate-600";
  return (
    <details className="border-t border-slate-600 py-4 text-left" data-testid="independent-pass-observation">
      <summary className="cursor-pointer font-semibold">Record an independent pass</summary>
      <div className="space-y-4 pt-4">
        <p className="text-sm text-slate-300">Record only what you observed in this lesson. Two separate lessons with different conditions are needed before you transfer responsibility. AI and video suggestions do not count as your observation.</p>
        {error && <p role="alert">{error}</p>}
        {message && <p role="status">{message}</p>}
        {!context && !error && <p>Loading this lesson…</p>}
        {!context && error && <button type="button" disabled={busy} onClick={() => act(async () => {}, "")} className={fieldClass}>Retry</button>}
        {context && <>
          <label className="block">Subject
            <select aria-label="Subject" className={fieldClass} value={competencyId} disabled={busy} onChange={(e) => {
              setCompetencyId(e.target.value); setResponsibilityId(""); setNote(""); setMessage("");
            }}>
              <option value="">Choose the subject you observed</option>
              {context.competencies.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>
          {competency && <label className="block">Responsibility
            <select aria-label="Responsibility" className={fieldClass} value={responsibilityId} disabled={busy} onChange={(e) => {
              setResponsibilityId(e.target.value); setNote(""); setPrompting(""); setConditions({}); setRevokeReason(""); setMessage("");
            }}>
              <option value="">Choose what the learner managed</option>
              {competency.responsibilities.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </label>}
          {responsibility && <>
            <p data-testid="independent-pass-count">{responsibility.gate.counted} of {responsibility.gate.required} independent passes with varied conditions.</p>
            {observation ? <>
              <p>This lesson's observation: {observation.observation_note}</p>
              <details><summary>Correct this observation</summary>
                <label className="block">Reason for withdrawing
                  <textarea aria-label="Reason for withdrawing" className={fieldClass} value={revokeReason} maxLength={500} disabled={busy} onChange={(e) => setRevokeReason(e.target.value)} />
                </label>
                <button type="button" className={fieldClass} disabled={busy || revokeReason.trim().length < 12} onClick={revoke}>Withdraw observation</button>
              </details>
            </> : <>
              <label className="block">Support during the attempt
                <select aria-label="Support during the attempt" className={fieldClass} value={prompting} disabled={busy} onChange={(e) => setPrompting(e.target.value)}>
                  <option value="">Choose the support you gave</option>
                  <option value="none">None — learner made the decisions</option>
                  <option value="light">Light reminder — learner still made the decisions</option>
                  <option value="material">I helped with a decision</option>
                  <option value="heavy">I directed the attempt</option>
                </select>
              </label>
              {["material", "heavy"].includes(prompting) && <p>This is useful practice, but cannot count as an independent pass.</p>}
              <fieldset disabled={busy}><legend>Conditions you observed</legend>
                {context.condition_dimensions.map((dimension, index) => <select key={dimension.join("-")} aria-label={`Condition ${index + 1}`} className={fieldClass} value={conditions[index] || ""} onChange={(e) => setConditions((old) => ({ ...old, [index]: e.target.value }))}>
                  <option value="">{dimension.map(label).join(" / ")} — not recorded</option>
                  {dimension.map((tag) => <option key={tag} value={tag}>{label(tag)}</option>)}
                </select>)}
              </fieldset>
              <label className="block">What did you observe?
                <textarea aria-label="What did you observe?" className={fieldClass} value={note} maxLength={500} disabled={busy} onChange={(e) => setNote(e.target.value)} />
              </label>
              <button type="button" className={fieldClass} disabled={busy || !["none", "light"].includes(prompting) || !tags.length || note.trim().length < 12} onClick={record}>Confirm I observed an independent pass</button>
            </>}
            {responsibility.owned ? <p>The learner owns this responsibility.</p> : <button type="button" className={fieldClass} disabled={busy || !responsibility.gate.satisfied} onClick={transfer}>Transfer responsibility to the learner</button>}
          </>}
        </>}
      </div>
    </details>
  );
}
